import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Lock, Mail } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@gabryx.ai");
  const [password, setPassword] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    const r = await login(email, password);
    if (r.ok) {
      toast.success("Bienvenue dans la console");
      navigate("/app");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2 relative overflow-hidden">
      {/* Left panel — form */}
      <div className="flex flex-col p-8 lg:p-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition mb-12 w-fit" data-testid="login-back-home">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>

        <div className="flex items-center gap-3 mb-16">
          <img src="/brand/gabryx-logo.png" alt="GABRYX" className="w-10 h-10 object-contain" />
          <span className="font-display font-semibold tracking-tight text-lg">GABRYX <span className="text-zinc-500">AI Suite</span></span>
        </div>

        <div className="max-w-md w-full">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Accès console</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-semibold mb-3" data-testid="login-title">
            Bienvenue,<br />commandant.
          </h1>
          <p className="text-zinc-400 mb-10">Vos agents IA vous attendent. Connectez-vous pour ouvrir la console.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="w-full bg-[#141414] border border-white/10 focus:border-white/40 outline-none px-10 py-3.5 transition font-mono text-sm"
                  placeholder="admin@gabryx.ai"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="w-full bg-[#141414] border border-white/10 focus:border-white/40 outline-none px-10 py-3.5 transition font-mono text-sm"
                  placeholder="••••••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-white text-black font-medium py-3.5 hover:bg-zinc-200 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Connexion…" : <>Entrer dans la console <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-xs text-zinc-600 font-mono leading-relaxed">
            🔐 Authentification JWT · connexion sécurisée via Cloudflare
          </div>
        </div>

        <div className="mt-auto pt-12 text-xs text-zinc-600 font-mono">
          © 2026 GABRYX · gabryxai.fr — pilote de CHANTIX
        </div>
      </div>

      {/* Right panel — visual */}
      <div className="hidden lg:flex relative bg-black border-l border-white/10 items-center justify-center grain">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/12198534/pexels-photo-12198534.jpeg')] bg-cover bg-center opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/60 to-black" />
        <div className="relative z-10 max-w-md p-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-6 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full pulse-dot" />
            système opérationnel
          </div>
          <blockquote className="font-display text-3xl tracking-tighter leading-tight mb-8">
            "Pour la première fois, j'ai l'impression de diriger une vraie équipe — sauf qu'elle ne dort jamais."
          </blockquote>
          <div className="border-t border-white/10 pt-6 text-sm text-zinc-400">
            — GABY, 06h17, lundi matin<br />
            <span className="text-zinc-600 font-mono text-xs">Première itération de la suite</span>
          </div>
        </div>
      </div>
    </div>
  );
}
